import type { HelixInputProps } from '@components/helixInput';
import HelixInput from '@components/helixInput';
import { useToggle } from '@hooks/useToggle';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { alpha, IconButton, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

// We can probably just Pick the props we want off of the MUI type.
type PasswordInputProps = {
  defaultValue?: unknown;
  defaultIsPasswordVisible?: boolean;
} & HelixInputProps;

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>((innerProps, ref) => {
  const {
    id,
    label,
    helperText,
    hasError,
    defaultIsPasswordVisible = false,
    value,
    placeholder = 'Password',
    onChange,
  } = innerProps;
  const [isPasswordVisible, togglePasswordVisible] = useToggle(defaultIsPasswordVisible);
  const passwordVisibilityIcon = isPasswordVisible ? <Visibility /> : <VisibilityOff />;

  const inputType = isPasswordVisible ? 'text' : 'password';

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  const endAdornment = (
    <InputAdornment position="end">
      <ToggleVisibilityIconButton
        size="large"
        aria-label="toggle password visibility"
        onClick={togglePasswordVisible}
        onMouseDown={handleMouseDown}
      >
        {passwordVisibilityIcon}
      </ToggleVisibilityIconButton>
    </InputAdornment>
  );

  return (
    <>
      <HelixInput
        id={id}
        placeholder={placeholder}
        label={label}
        variant="outlined"
        type={inputType}
        hasError={hasError}
        helperText={helperText}
        endAdornment={endAdornment}
        autoComplete="current-password"
        ref={ref}
        value={value}
        onChange={onChange}
      />
    </>
  );
});

const ToggleVisibilityIconButton = styled(IconButton)(({ theme }) => {
  return {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
    width: '40px',
    height: '40px',
    '&:hover': {
      backgroundColor: alpha(theme.palette.black.main, 0.04),
    },
    '&:focus': {
      backgroundColor: alpha(theme.palette.black.main, 0.04),
    },
  };
});

export { PasswordInput as default, PasswordInput };
export type { PasswordInputProps };
